import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe, Download, ChevronDown, ChevronUp } from "lucide-react";

const B = process.env.REACT_APP_BACKEND_URL;
const fmtDur = (s) => (s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function VisitorLog() {
  const [days, setDays] = useState(7);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/admin/visits?days=${days}&page=${page}&limit=50${q ? `&q=${encodeURIComponent(q)}` : ""}`).then(({ data }) => setData(data)).catch(() => setData({ items: [], summary: {}, top_pages: [], countries: [], total: 0 })), 250);
    return () => clearTimeout(t);
  }, [days, q, page]);
  const s = data?.summary || {};
  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="visitor-log-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><Globe className="h-4 w-4 text-[#007AFF]" /><h2 className="font-black text-xl tracking-tight">Visitor log</h2></div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search IP, city, email, page…" data-testid="visits-search" className="h-8 w-56 rounded-md bg-black/40 border border-white/10 px-2 text-xs" />
          {[1, 7, 30, 90].map((d) => <button key={d} onClick={() => { setDays(d); setPage(1); }} data-testid={`visits-days-${d}`} className={`px-3 py-1 rounded-full text-xs border ${days === d ? "bg-white text-black border-white" : "text-neutral-400 border-white/15 hover:bg-white/5"}`}>{d}d</button>)}
          <a href={`${B}/api/admin/visits/export.csv?days=${days}`} data-testid="visits-export" className="h-8 px-3 rounded-full border border-white/15 text-xs inline-flex items-center gap-1 hover:bg-white/5"><Download className="h-3 w-3" /> CSV</a>
        </div>
      </div>
      <p className="mt-1 text-xs text-neutral-500">Every browsing session: IP address, approximate location, device, the account signed in from it (and every account ever seen on that IP), date &amp; time, pages navigated and time spent. Kept 180 days.</p>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        {[["visits", "Visits"], ["unique_ips", "Unique IPs"], ["pages", "Pages viewed"], ["avg_duration_s", "Avg. time"], ["signed_in", "Signed-in visits"]].map(([k, l]) => (
          <div key={k} className="rounded-lg bg-black/40 border border-white/10 p-2 text-center" data-testid={`visits-total-${k}`}>
            <div className="font-black text-lg">{k === "avg_duration_s" ? fmtDur(s[k] || 0) : (s[k] ?? 0)}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{l}</div>
          </div>
        ))}
      </div>
      {data && (data.top_pages?.length > 0 || data.countries?.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-neutral-400">
          {data.countries?.length > 0 && <span>Countries: {data.countries.map((c) => `${c.country} ${c.n}`).join(" · ")}</span>}
          {data.top_pages?.length > 0 && <span>Top pages: {data.top_pages.slice(0, 6).map((p) => `${p.path} (${p.n})`).join(" · ")}</span>}
        </div>
      )}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs" data-testid="visits-table">
          <thead><tr className="text-left text-neutral-500 uppercase tracking-widest text-[10px]"><th className="py-2 pr-3">Date &amp; time</th><th className="py-2 pr-3">IP</th><th className="py-2 pr-3">Location</th><th className="py-2 pr-3">Account(s)</th><th className="py-2 pr-3">Device</th><th className="py-2 pr-3 text-right">Pages</th><th className="py-2 pr-3 text-right">Duration</th><th></th></tr></thead>
          <tbody>
            {(data?.items || []).map((v) => (
              <VisitRow key={v.id} v={v} open={open === v.id} onToggle={() => setOpen(open === v.id ? null : v.id)} />
            ))}
            {data && data.items.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-neutral-500" data-testid="visits-empty">No visits recorded in this period yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && data.total > 50 && (
        <div className="mt-3 flex items-center justify-between text-xs text-neutral-400">
          <span>{data.total} sessions</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded-full border border-white/15 disabled:opacity-40" data-testid="visits-prev">Prev</button>
            <button disabled={page * 50 >= data.total} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded-full border border-white/15 disabled:opacity-40" data-testid="visits-next">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function VisitRow({ v, open, onToggle }) {
  return (
    <>
      <tr className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={onToggle} data-testid={`visit-row-${v.id}`}>
        <td className="py-2 pr-3 whitespace-nowrap text-neutral-300">{fmtTime(v.started_at)}</td>
        <td className="py-2 pr-3 font-mono text-white">{v.ip}</td>
        <td className="py-2 pr-3 text-neutral-300">{[v.city, v.country].filter(Boolean).join(", ") || "—"}</td>
        <td className="py-2 pr-3">{v.accounts?.length ? v.accounts.map((e) => <span key={e} className={`inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-white/5 ${e === v.user_email ? "text-[#34C759]" : "text-neutral-400"}`}>{e}</span>) : <span className="text-neutral-600">guest</span>}</td>
        <td className="py-2 pr-3 text-neutral-400">{[v.browser, v.os].filter(Boolean).join(" · ")}</td>
        <td className="py-2 pr-3 text-right text-neutral-300">{v.page_count}</td>
        <td className="py-2 pr-3 text-right text-neutral-300">{fmtDur(v.duration_s)}</td>
        <td className="py-2 text-neutral-500">{open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</td>
      </tr>
      {open && (
        <tr className="bg-black/30" data-testid={`visit-pages-${v.id}`}>
          <td colSpan={8} className="px-3 py-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Pages navigated{v.referrer ? ` · came from ${v.referrer}` : ""}</div>
            <ol className="space-y-0.5">
              {v.pages.map((p, i) => <li key={i} className="flex gap-3 text-neutral-300"><span className="text-neutral-500 font-mono w-20">{p.at ? new Date(p.at).toLocaleTimeString() : ""}</span><span className="font-mono">{p.path}</span></li>)}
            </ol>
          </td>
        </tr>
      )}
    </>
  );
}
